import { useContext, type ReactNode } from "react";
import { PermissionContext } from "@/contexts/permission-context";
import { UsersRound } from "lucide-react";
import { Link } from "react-router-dom";

import { PageHeader } from "@/components/ui";
import { cn } from "@/lib/utils";

import { SETTINGS_SECTIONS, type SettingsSectionId } from "./settings-navigation";

type SettingsLayoutProps = {
  current: SettingsSectionId;
  title: ReactNode;
  lede: ReactNode;
  /**
   * Where the clinic came from. SCRUM-208 (audit C-13): "Canais" is reached
   * from the AI team group, and a fixed "Configurações" eyebrow announced a
   * different place than the menu item that had just been clicked.
   */
  eyebrow?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
};

/**
 * The chrome the settings sections share: the page header of the design
 * system (eyebrow → display title → lede) and one sub-navigation, scrollable
 * on top for narrow screens and vertical on the left from `lg` up.
 */
export function SettingsLayout({
  current,
  title,
  lede,
  eyebrow = "Configurações",
  actions,
  children,
}: SettingsLayoutProps) {
  const permissions = useContext(PermissionContext);
  const canViewUsers = permissions?.checkPermission("user", "view user") ?? false;
  const canViewProfiles = permissions?.checkPermission("role", "view role") ?? false;
  const sections = [...SETTINGS_SECTIONS];
  if (canViewUsers || canViewProfiles) {
    sections.splice(2, 0, { id: "team", title: "Equipe e acessos", url: canViewUsers ? "/registrations/users" : "/permissions", icon: UsersRound });
  }
  return (
    <section className="mx-auto max-w-6xl space-y-6 pb-12">
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        lede={lede}
        actions={actions}
      />

      <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-8">
        <nav
          aria-label="Seções de configurações"
          className="-mx-1 overflow-x-auto px-1 lg:mx-0 lg:overflow-visible lg:px-0"
        >
          <ul className="flex min-w-max gap-1 lg:min-w-0 lg:flex-col lg:gap-0.5">
            {sections.map((section) => {
              const selected = section.id === current;
              const Icon = section.icon;
              return (
                <li key={section.id}>
                  <Link
                    to={section.url}
                    aria-current={selected ? "page" : undefined}
                    className={cn(
                      "flex h-10 items-center gap-2 rounded-lg px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1",
                      selected
                        ? "bg-brand-soft font-medium text-brand"
                        : "text-app-fg-muted hover:bg-app-surface-muted hover:text-app-fg"
                    )}
                  >
                    <Icon className="size-4 shrink-0" aria-hidden="true" />
                    {section.title}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="min-w-0">{children}</div>
      </div>
    </section>
  );
}
